/**
 * Admin API Service
 * 
 * Handles API calls for admin functionality with proper authentication
 */

import { apiService } from './api';
import { adminAuthService } from './adminAuth';

export interface DashboardStats {
  name: string;
  value: string;
  change: string;
  changeType: 'increase' | 'decrease';
  icon: string;
  todayCount: number;
}

export interface SystemHealth {
  system: {
    status: string;
    uptime: number;
    uptimeFormatted: string;
    timestamp: string;
  };
  database: {
    status: string;
    responseTime: number;
    collections: {
      users: number;
      urls: number;
      clicks: number;
    };
  };
  memory: {
    used: number;
    total: number;
    rss: number;
  };
  activity: {
    last24Hours: {
      newUsers: number;
      newUrls: number;
      clicks: number;
    };
  };
}

export interface UserManagementData {
  users: Array<{
    _id: string;
    email: string;
    name?: string;
    role: string;
    isActive: boolean;
    plan: string;
    createdAt: string;
    lastLoginAt?: string;
    stats: {
      totalUrls: number;
      totalClicks: number;
    };
  }>;
  pagination: {
    page: number;
    limit: number;
    totalUsers: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

class AdminApiService {
  private getAuthHeaders() {
    const token = adminAuthService.getToken();
    if (!token) {
      throw new Error('No admin token available');
    }
    return {
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Get dashboard statistics
   */
  async getDashboardStats(): Promise<DashboardStats[]> {
    try {
      const response = await apiService.client.get<{
        success: boolean;
        data: {
          stats: DashboardStats[];
          timestamp: string;
        };
      }>('/admin/dashboard/stats', {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error('Failed to fetch dashboard stats');
      }

      return response.data.data.stats;
    } catch (error: any) {
      console.error('Failed to fetch dashboard stats:', error);
      throw error;
    }
  }

  /**
   * Get system health information
   */
  async getSystemHealth(): Promise<SystemHealth> {
    try {
      const response = await apiService.client.get<{
        success: boolean;
        data: SystemHealth;
      }>('/admin/system/health', {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error('Failed to fetch system health');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch system health:', error);
      throw error;
    }
  }

  /**
   * Get user management data
   */
  async getUsers(params: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    plan?: string;
    isActive?: string;
    isVerified?: string;
    role?: string;
  } = {}): Promise<UserManagementData> {
    try {
      const searchParams = new URLSearchParams();
      
      if (params.page) {
        searchParams.append('page', params.page.toString())
      }
      if (params.limit) {
        searchParams.append('limit', params.limit.toString())
      }
      if (params.search) {
        searchParams.append('search', params.search)
      }
      if (params.sortBy) {
        searchParams.append('sortBy', params.sortBy)
      }
      if (params.sortOrder) {
        searchParams.append('sortOrder', params.sortOrder)
      }
      if (params.plan) {
        searchParams.append('plan', params.plan)
      }
      if (params.isActive) {
        searchParams.append('isActive', params.isActive)
      }
      if (params.isVerified) {
        searchParams.append('isVerified', params.isVerified)
      }
      if (params.role) {
        searchParams.append('role', params.role)
      }

      const response = await apiService.client.get<{
        success: boolean;
        data: UserManagementData;
      }>(`/admin/users?${searchParams.toString()}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error('Failed to fetch users');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      throw error;
    }
  }

  /**
   * Update user status (activate/deactivate)
   */
  async updateUserStatus(userId: string, isActive: boolean): Promise<void> {
    try {
      const response = await apiService.client.patch<{
        success: boolean;
        message: string;
      }>(`/admin/users/${userId}/status`, 
      { isActive }, 
      {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to update user status');
      }
    } catch (error: any) {
      console.error('Failed to update user status:', error);
      throw error;
    }
  }

  /**
   * Get single user details
   */
  async getUserDetails(userId: string): Promise<any> {
    try {
      console.log('Fetching user details for ID:', userId); // Debug log
      
      const response = await apiService.client.get<{
        success: boolean;
        data: any;
      }>(`/admin/users/${userId}`, {
        headers: this.getAuthHeaders()
      });

      console.log('API response:', response.data); // Debug log

      if (!response.data.success) {
        throw new Error('Failed to fetch user details');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch user details:', error);
      console.error('Error response:', error.response?.data); // Debug log
      throw error;
    }
  }

  /**
   * Get user URLs with pagination
   */
  async getUserUrls(userId: string, params: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  } = {}): Promise<any> {
    try {
      const searchParams = new URLSearchParams();
      
      if (params.page) {
        searchParams.append('page', params.page.toString())
      }
      if (params.limit) {
        searchParams.append('limit', params.limit.toString())
      }
      if (params.sortBy) {
        searchParams.append('sortBy', params.sortBy)
      }
      if (params.sortOrder) {
        searchParams.append('sortOrder', params.sortOrder)
      }

      const response = await apiService.client.get<{
        success: boolean;
        data: any;
      }>(`/admin/users/${userId}/urls?${searchParams.toString()}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error('Failed to fetch user URLs');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch user URLs:', error);
      throw error;
    }
  }

  /**
   * Get user analytics data
   */
  async getUserAnalytics(userId: string, days: number = 30): Promise<any> {
    try {
      const response = await apiService.client.get<{
        success: boolean;
        data: any;
      }>(`/admin/users/${userId}/analytics?days=${days}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error('Failed to fetch user analytics');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch user analytics:', error);
      throw error;
    }
  }

  /**
   * Get user deletion preview
   */
  async getUserDeletionPreview(userId: string): Promise<any> {
    try {
      const response = await apiService.client.get<{
        success: boolean;
        data: any;
      }>(`/admin/users/${userId}/deletion-preview`, {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error('Failed to fetch deletion preview');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch deletion preview:', error);
      throw error;
    }
  }

  /**
   * Delete single user and all related data
   */
  async deleteUser(userId: string, confirmDelete: boolean = true): Promise<any> {
    try {
      const response = await apiService.client.delete<{
        success: boolean;
        message: string;
        data: any;
      }>(`/admin/users/${userId}`, {
        headers: this.getAuthHeaders(),
        data: { confirmDelete }
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete user');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      throw error;
    }
  }

  /**
   * Bulk delete users
   */
  async bulkDeleteUsers(userIds: string[], confirmDelete: boolean = true): Promise<any> {
    try {
      const response = await apiService.client.post<{
        success: boolean;
        message: string;
        data: any;
      }>('/admin/users/bulk-delete', {
        userIds,
        confirmDelete
      }, {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Bulk deletion failed');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Bulk deletion failed:', error);
      throw error;
    }
  }

  /**
   * Bulk user actions
   */
  async bulkUserAction(userIds: string[], action: string, data?: any): Promise<void> {
    try {
      const response = await apiService.client.post<{
        success: boolean;
        message: string;
      }>('/admin/users/bulk-action', {
        userIds,
        action,
        data
      }, {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Bulk action failed');
      }
    } catch (error: any) {
      console.error('Bulk action failed:', error);
      throw error;
    }
  }

  /**
   * Get user statistics for charts
   */
  async getUserStats(period: 'day' | 'week' | 'month' | 'year' = 'month'): Promise<any> {
    try {
      const response = await apiService.client.get<{
        success: boolean;
        data: any;
      }>(`/admin/analytics/users?period=${period}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error('Failed to fetch user statistics');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch user statistics:', error);
      throw error;
    }
  }

  /**
   * Get detailed analytics data
   */
  async getAnalytics(period: 'today' | 'week' | 'month' | 'year' = 'month'): Promise<any> {
    try {
      const response = await apiService.client.get<{
        success: boolean;
        data: any;
      }>(`/admin/analytics?period=${period}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error('Failed to fetch analytics');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch analytics:', error);
      throw error;
    }
  }

  /**
   * Get security alerts and events (improved with real-time data)
   */
  async getSecurityAlerts(): Promise<any[]> {
    try {
      const response = await apiService.client.get<{
        success: boolean;
        data: any[];
      }>('/admin/security/alerts', {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error('Failed to fetch security alerts');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch security alerts:', error);
      // Return empty array if endpoint doesn't exist yet
      if (error.response?.status === 404) {
        return [];
      }
      throw error;
    }
  }

  /**
   * Get real-time security metrics
   */
  async getSecurityMetrics(): Promise<any> {
    try {
      const response = await apiService.client.get<{
        success: boolean;
        data: any;
      }>('/admin/security/metrics', {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error('Failed to fetch security metrics');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch security metrics:', error);
      // Return fallback data if endpoint doesn't exist
      if (error.response?.status === 404) {
        return {
          totalThreats: 0,
          blockedRequests: 0,
          activeSecurityRules: 0,
          systemIntegrity: 100,
          encryptionStatus: 'enabled',
          sslCertStatus: 'valid',
          backupStatus: 'healthy'
        };
      }
      throw error;
    }
  }

  /**
   * Export data in various formats
   */
  async exportData(type: 'users' | 'urls' | 'analytics', format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
    try {
      const response = await apiService.client.get(
        `/admin/export/${type}?format=${format}`, 
        {
          headers: this.getAuthHeaders(),
          responseType: 'blob'
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Failed to export data:', error);
      throw error;
    }
  }

  /**
   * Get system configuration
   */
  async getSystemConfig(): Promise<any> {
    try {
      const response = await apiService.client.get<{
        success: boolean;
        data: any;
      }>('/admin/config', {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error('Failed to fetch system config');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch system config:', error);
      throw error;
    }
  }

  /**
   * Update system configuration
   */
  async updateSystemConfig(config: any): Promise<void> {
    try {
      const response = await apiService.client.put<{
        success: boolean;
        message: string;
      }>('/admin/config', config, {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to update system config');
      }
    } catch (error: any) {
      console.error('Failed to update system config:', error);
      throw error;
    }
  }

  /**
   * Search users for API key management (Growth+ plans only)
   */
  async searchUsers(query: string): Promise<{
    users: Array<{
      _id: string;
      email: string;
      name: string;
      plan: string;
    }>;
  }> {
    try {
      const response = await apiService.client.get<{
        success: boolean;
        data: {
          users: Array<{
            _id: string;
            email: string;
            name: string;
            plan: string;
          }>;
        };
      }>(`/admin/users/search?q=${encodeURIComponent(query)}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error('Failed to search users');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to search users:', error);
      throw error;
    }
  }

  // =============================================
  // LINK MANAGEMENT METHODS
  // =============================================

  /**
   * Get all links with pagination, search, and filters
   */
  async getLinks(params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: 'all' | 'active' | 'inactive';
    sortBy?: 'createdAt' | 'clicks' | 'lastClickAt';
    sortOrder?: 'asc' | 'desc';
  }): Promise<{
    links: any[];
    stats: any;
    pagination: any;
  }> {
    try {
      const response = await apiService.client.get<{
        success: boolean;
        data: {
          links: any[];
          stats: any;
          pagination: any;
        };
      }>('/admin/links', {
        params,
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error('Failed to fetch links');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch links:', error);
      throw error;
    }
  }

  /**
   * Update link status
   */
  async updateLinkStatus(linkId: string, isActive: boolean): Promise<void> {
    try {
      const response = await apiService.client.patch<{
        success: boolean;
        message: string;
      }>(`/admin/links/${linkId}/status`, { isActive }, {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to update link status');
      }
    } catch (error: any) {
      console.error('Failed to update link status:', error);
      throw error;
    }
  }

  /**
   * Bulk update link status
   */
  async bulkUpdateLinkStatus(linkIds: string[], isActive: boolean): Promise<void> {
    try {
      const response = await apiService.client.patch<{
        success: boolean;
        message: string;
      }>('/admin/links/bulk-status', { linkIds, isActive }, {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to bulk update link status');
      }
    } catch (error: any) {
      console.error('Failed to bulk update link status:', error);
      throw error;
    }
  }

  /**
   * Get link deletion preview
   */
  async getLinkDeletionPreview(linkId: string): Promise<any> {
    try {
      const response = await apiService.client.get<{
        success: boolean;
        data: any;
      }>(`/admin/links/${linkId}/deletion-preview`, {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error('Failed to get link deletion preview');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to get link deletion preview:', error);
      throw error;
    }
  }

  /**
   * Delete link
   */
  async deleteLink(linkId: string): Promise<any> {
    try {
      const response = await apiService.client.delete<{
        success: boolean;
        message: string;
        data: any;
      }>(`/admin/links/${linkId}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete link');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to delete link:', error);
      throw error;
    }
  }

  /**
   * Calculate proration for plan change
   */
  async calculateProration(params: {
    userId: string;
    newPlanId: string;
    newBilling: 'monthly' | 'yearly';
  }): Promise<any> {
    try {
      const response = await apiService.client.get<{
        success: boolean;
        data: any;
        message?: string;
      }>(`/admin/users/${params.userId}/proration-calculation`, {
        params: {
          newPlanId: params.newPlanId,
          newBilling: params.newBilling
        },
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to calculate proration');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to calculate proration:', error);
      throw error;
    }
  }

  /**
   * Change user plan
   */
  async changeUserPlan(params: {
    userId: string;
    planId: string;
    billing: 'monthly' | 'yearly';
    startImmediately?: boolean;
  }): Promise<any> {
    try {
      const response = await apiService.client.put<{
        success: boolean;
        message: string;
        data: any;
      }>(`/admin/users/${params.userId}/change-plan`, {
        planId: params.planId,
        billing: params.billing,
        startImmediately: params.startImmediately
      }, {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to change user plan');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to change user plan:', error);
      throw error;
    }
  }

  /**
   * Bulk delete links
   */
  async bulkDeleteLinks(linkIds: string[]): Promise<any> {
    try {
      const response = await apiService.client.delete<{
        success: boolean;
        message: string;
        data: any;
      }>('/admin/links/bulk', {
        data: { linkIds },
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to bulk delete links');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to bulk delete links:', error);
      throw error;
    }
  }

  // =============================================
  // PLAN MANAGEMENT METHODS
  // =============================================

  /**
   * Get available plans
   */
  async getPlans(): Promise<any[]> {
    try {
      const response = await apiService.client.get<{
        success: boolean;
        data: {
          plans: any[];
        };
      }>('/admin/plan-management/plans', {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error('Failed to fetch plans');
      }

      return response.data.data.plans;
    } catch (error: any) {
      console.error('Failed to fetch plans:', error);
      throw error;
    }
  }

  /**
   * Change user plan (new plan management API)
   */
  async changePlan(params: {
    userId: string;
    newPlanType: string;
    reason?: string;
    notifyUser?: boolean;
  }): Promise<any> {
    try {
      const response = await apiService.client.post<{
        success: boolean;
        message: string;
        data: any;
      }>('/admin/plan-management/change-plan', {
        userId: params.userId,
        newPlanType: params.newPlanType,
        reason: params.reason,
        notifyUser: params.notifyUser ?? true
      }, {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to change plan');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to change plan:', error);
      throw error;
    }
  }

  /**
   * Get plan change history for a specific user
   */
  async getUserPlanHistory(userId: string, params: {
    page?: number;
    limit?: number;
  } = {}): Promise<any> {
    try {
      const searchParams = new URLSearchParams();
      if (params.page) {
        searchParams.append('page', params.page.toString());
      }
      if (params.limit) {
        searchParams.append('limit', params.limit.toString());
      }

      const response = await apiService.client.get<{
        success: boolean;
        data: any;
      }>(`/admin/plan-management/plan-history/${userId}?${searchParams.toString()}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error('Failed to fetch plan history');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch plan history:', error);
      throw error;
    }
  }

  /**
   * Get all plan change history with filters
   */
  async getAllPlanHistory(params: {
    page?: number;
    limit?: number;
    planType?: string;
    changedBy?: string;
    dateFrom?: string;
    dateTo?: string;
    method?: string;
  } = {}): Promise<any> {
    try {
      const searchParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          searchParams.append(key, value.toString());
        }
      });

      const response = await apiService.client.get<{
        success: boolean;
        data: any;
      }>(`/admin/plan-management/all-plan-history?${searchParams.toString()}`, {
        headers: this.getAuthHeaders()
      });

      if (!response.data.success) {
        throw new Error('Failed to fetch all plan history');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to fetch all plan history:', error);
      throw error;
    }
  }
}

// Create and export singleton instance
export const adminApiService = new AdminApiService();
